import { useState } from "react";
import type { QuickBooksCashTieOut } from "../../../data/ServerApiClient";

/**
 * The weekly reconciliation strip, above the 13-week table.
 *
 * Rob Lund reconciles the bank weekly, immediately before producing the
 * forecast — so this is a gate on the number, not a footnote about it.
 *
 * The default state is an INVITATION, never an alarm. A workbook that has never
 * keyed a balance is not in trouble; it just has not done this yet, and dressing
 * that as a warning teaches people to ignore the warnings that matter. It
 * escalates to the error treatment on exactly one condition — the unexplained
 * difference is larger than the minimum-cash floor, i.e. large enough to change
 * the answer to "will I make payroll". That is a principled threshold rather
 * than a mood.
 */
export type CfoCashTieOutStripProps = {
  tieOut?: QuickBooksCashTieOut | null;
  canEdit?: boolean;
  onKeyBalance?: (balanceDate: string, bankBalance: number) => Promise<void>;
  currency?: (amount: number) => string;
};

const TONE: Record<string, { border: string; tint: string; label: string }> = {
  ties: { border: "var(--pos, #1a7f5a)", tint: "8%", label: "Reconciled" },
  float: { border: "var(--acc, #176b87)", tint: "8%", label: "In transit" },
  // NOT red by default. An unexplained difference below the floor is worth
  // naming and is not worth alarming about; the `alarming` branch below is the
  // only thing that reaches for --neg. Hard-coding red here made the status
  // itself the trigger and quietly bypassed the floor rule entirely.
  unexplained: { border: "var(--acc, #176b87)", tint: "8%", label: "Unexplained" },
  out_of_window: { border: "var(--line, #e5e0d8)", tint: "0%", label: "Not comparable" },
  unkeyed: { border: "var(--line, #e5e0d8)", tint: "0%", label: "Not reconciled yet" },
};

export default function CfoCashTieOutStrip({
  tieOut,
  canEdit = false,
  onKeyBalance,
  currency = (amount) => amount.toFixed(2),
}: CfoCashTieOutStripProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [balance, setBalance] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const status = tieOut?.status || "unkeyed";
  const tone = TONE[status] || TONE.unkeyed;
  // Only `unexplained` gets the error treatment, and only when the server said
  // it clears the floor. `exceeds_floor` is the server's judgement, not a
  // threshold recomputed here — two copies of that rule would drift.
  const alarming = status === "unexplained" && tieOut?.exceeds_floor === true;

  const submit = async () => {
    // `Number('')` and `Number('   ')` are both 0, and `Number.isFinite(0)` is
    // true — so a finiteness check alone accepts an EMPTY field and keys a
    // balance of zero. That would report the company's entire cash balance as
    // an unexplained difference and, past the floor, paint the strip red.
    // Emptiness has to be rejected before the number is parsed.
    const raw = balance.trim();
    const parsed = Number(raw);
    if (!raw || !Number.isFinite(parsed)) {
      setError("Enter the balance as a number.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onKeyBalance?.(date, parsed);
      setOpen(false);
      setBalance("");
    } catch (err: any) {
      setError(err?.message || "Could not save that balance.");
    } finally {
      setSaving(false);
    }
  };

  const float = tieOut?.float_amount != null ? Number(tieOut.float_amount) : null;

  return (
    <div
      data-testid="cash-tie-out-strip"
      data-status={status}
      style={{
        border: `1px solid ${alarming ? "var(--neg, #b91c1c)" : tone.border}`,
        background: alarming
          ? "color-mix(in srgb, var(--neg, #b91c1c) 8%, transparent)"
          : tone.tint === "0%"
            ? "transparent"
            : `color-mix(in srgb, ${tone.border} ${tone.tint}, transparent)`,
        borderRadius: 8,
        padding: "8px 12px",
        display: "grid",
        gap: 6,
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: alarming ? "var(--neg, #b91c1c)" : "var(--muted, #868c9a)",
          }}
        >
          {tone.label}
        </span>
        <span style={{ fontSize: 13 }}>
          {tieOut
            ? tieOut.message
            : "Record this week's bank balance before you read the forecast — it is what the opening cash is measured against."}
        </span>
        {float != null && status !== "ties" ? (
          <span
            data-testid="cash-tie-out-float"
            style={{ fontSize: 13, fontWeight: 600, marginLeft: "auto" }}
          >
            {currency(float)}
          </span>
        ) : null}
      </div>

      {canEdit ? (
        open ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="date"
              aria-label="Balance date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              style={{ font: "inherit", fontSize: 13, padding: "4px 6px" }}
            />
            <input
              type="text"
              inputMode="decimal"
              aria-label="Bank balance"
              placeholder="Balance from the statement"
              value={balance}
              onChange={(event) => setBalance(event.target.value)}
              style={{ font: "inherit", fontSize: 13, padding: "4px 6px", minWidth: 180 }}
            />
            <button type="button" onClick={submit} disabled={saving} style={{ font: "inherit", fontSize: 13 }}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError("");
              }}
              style={{ font: "inherit", fontSize: 13, background: "none", border: 0, color: "var(--muted, #868c9a)" }}
            >
              Cancel
            </button>
            {error ? (
              <span style={{ fontSize: 12, color: "var(--neg, #b91c1c)" }}>{error}</span>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{
              font: "inherit",
              fontSize: 12,
              background: "none",
              border: 0,
              padding: 0,
              color: "var(--acc, #176b87)",
              cursor: "pointer",
              justifySelf: "start",
            }}
          >
            {tieOut ? "Record a new balance" : "Record this week's balance"}
          </button>
        )
      ) : null}
    </div>
  );
}
